import { useEffect, useReducer, useState } from "react";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "react-bootstrap";
import Select from "react-select";
import { optionSelector } from "../../../../config/apiSPA";
/**
 * @typedef {Object} CompetencyData
 * @property {number} trainingId
 * @property {number} kodeTrainingId
 * @property {number} passingScore
 * @property {string} remark
 */
/**
 * @param {{
 * show: boolean,
 * onHide: () => void,
 * title?: string,
 * data?: CompetencyData,
 * submitClick: (data: CompetencyData) => void
 * }} param0
 * @returns {JSX.Element}
 */
function ModalAddEditCompetency({ show, onHide, title = "Add Competency", data, submitClick }) {
  const [formData, setFormData] = useState({
    trainingId: data?.trainingId ?? null,
    kodeTrainingId: data?.kodeTrainingId ?? null,
    passingScore: data?.passingScore ?? "",
    remark: data?.remark ?? "",
  });

  const [competencyOptions, setCompetencyOptions] = useState([]);
  const [isLoadingCompetency, setIsLoadingCompetency] = useState(false);

  // Reset form ketika modal dibuka / data berubah
  useEffect(() => {
    if (!show) return;

    setFormData({
      trainingId: data?.trainingId ?? null,
      kodeTrainingId: data?.kodeTrainingId ?? null,
      passingScore: data?.passingScore ?? "",
      remark: data?.remark ?? "",
    });
  }, [show, data]);

  // Load competency
  useEffect(() => {
    if (!show) return;

    const loadCompetency = async () => {
      try {
        setIsLoadingCompetency(true);

        const res = await optionSelector("/trainingTitle");

        setCompetencyOptions(res);
      } catch (error) {
        console.error("Failed to load competency:", error);
        setCompetencyOptions([]);
      } finally {
        setIsLoadingCompetency(false);
      }
    };

    loadCompetency();
  }, [show]);

  const handleChange = (event) => {
    const { id, value } = event.target;

    setFormData((prev) => ({
      ...prev,
      [id]: value,
    }));
  };

  const handleCompetencyChange = (selectedOption) => {
    setFormData((prev) => ({
      ...prev,
      kodeTrainingId: selectedOption?.value ?? null,
    }));
  };

  const handleClickSubmit = () => {
    if (!isFormValid) return;

    submitClick({
      trainingId: formData.trainingId,
      kodeTrainingId: formData.kodeTrainingId,
      passingScore: Number(formData.passingScore),
      remark: formData.remark,
    });
  };

  const isFormValid = formData.trainingId !== null && formData.kodeTrainingId !== null && formData.passingScore !== "" && formData.passingScore >= 0 && formData.passingScore <= 100;

  const selectedCompetency = competencyOptions.find((option) => option.value === formData.kodeTrainingId) ?? null;

  return (
    <Modal show={show} onHide={onHide}>
      <ModalHeader onHide={onHide}>{title}</ModalHeader>
      <ModalBody>
        {/* Competency */}
        <div className="form-group mb-3">
          <label className="form-label" htmlFor="competency">
            Competency
          </label>

          <Select
            inputId="competency"
            classNamePrefix="react-select"
            isSearchable
            isClearable
            isLoading={isLoadingCompetency}
            isDisabled={isLoadingCompetency}
            options={competencyOptions}
            value={selectedCompetency}
            onChange={handleCompetencyChange}
            placeholder={isLoadingCompetency ? "Loading competency..." : "Select competency..."}
            noOptionsMessage={() => "Competency not found"}
          />
        </div>

        {/* Passing Score */}
        <div className="form-group mb-3">
          <label className="form-label" htmlFor="passingScore">
            Passing Score (0 - 100)
          </label>

          <input type="number" className="form-control" id="passingScore" min={0} max={100} value={formData.passingScore} onChange={handleChange} />
        </div>

        {/* Remark */}
        <div className="form-group mb-3">
          <label className="form-label" htmlFor="remark">
            Remark
          </label>

          <textarea className="form-control" id="remark" value={formData.remark} onChange={handleChange} />
        </div>
      </ModalBody>

      <ModalFooter>
        <button onClick={onHide} type="button" className="btn btn-secondary">
          Cancel
        </button>

        <button onClick={handleClickSubmit} type="button" className="btn btn-outline-theme" disabled={!isFormValid}>
          Submit
        </button>
      </ModalFooter>
    </Modal>
  );
}

export default ModalAddEditCompetency;
